import React, { useState, useRef, useEffect } from 'react';
import SEO from './SEO';
import { generateOrganizationSchema, generateWebsiteSchema } from '../utils/structuredData';

// Endpoint del CMS que procesa la inscripción (ruta de plugin sin CSRF que devuelve JSON).
// Definido en plugins/remiblot/cmsadditions/routes-pf.php del CMS Winter.
const PF_ENDPOINT = 'https://discover.af-pictures.com/api/pf-submit';
// URL del PDF con las bases legales del concurso (enlace de descarga dentro del modal).
const BASES_PDF_URL = 'https://afpictures.com/storage/app/media/AF%20-%20Bases%20de%20la%20I%20EDICI%C3%93N%20DEL%20CONCURSO%20DE%20GUIONES%20DE%20CORTOMETRAJES.pdf';

// Límite de subida (debe coincidir con el endpoint del CMS)
const MAX_TOTAL_MB = 14; // máximo del conjunto de todos los documentos

// Imágenes del concurso (storage del CMS)
const IMG_BASE = 'https://discover.af-pictures.com/storage/app/media/upload-img/';
const IMG = {
  logo: IMG_BASE + 'summer-screams-logo-sin-fondo.png',
  logoAF: IMG_BASE + 'Logo%20Af%20blanco.png',
  poster: IMG_BASE + 'Poster%20Summer%20Screams%20(logo%20m%C3%A1s%20visible).png',
  titulo: IMG_BASE + 'Titulo%20de%20concurso%20verano.png',
  vestido: IMG_BASE + 'EL%20VESTIDO_IMG.png',
  silla: IMG_BASE + 'Poster%20La%20Silla.jpg',
  salpicaduraIzq: IMG_BASE + 'Salpicadura%20izquierda.png',
  salpicaduraDer: IMG_BASE + 'Salpicadura%20derecha.png',
};

const GENERO_OPTIONS = ['Femenino', 'Masculino', 'No binario/otro', 'Prefiero no decirlo'];

// Viñeta con chevron rojo usada en el listado de "En qué consiste".
const Bullet = ({ children }) => (
  <li className="flex gap-3">
    <span aria-hidden="true" className="text-red-600 select-none">›</span>
    <span>{children}</span>
  </li>
);

const GUIONISTA_FIELDS = [
  { name: 'nombre', label: 'Nombre', type: 'text' },
  { name: 'apellidos', label: 'Apellidos', type: 'text' },
  { name: 'nacionalidad', label: 'Nacionalidad', type: 'text' },
  { name: 'fecha_nacimiento', label: 'Fecha de nacimiento', type: 'date' },
  { name: 'domicilio', label: 'Domicilio', type: 'text' },
  { name: 'localidad', label: 'Localidad', type: 'text' },
  { name: 'cp', label: 'Código Postal', type: 'text' },
  { name: 'provincia', label: 'Provincia', type: 'text' },
  { name: 'genero', label: 'Género', type: 'select' },
  { name: 'centro_estudios', label: 'Centro de estudios', type: 'text' },
  { name: 'estudios', label: 'Estudios', type: 'text' },
  { name: 'dni', label: 'DNI', type: 'text' },
  { name: 'telefono', label: 'Teléfono', type: 'tel' },
  { name: 'email', label: 'Mail', type: 'email' },
];

// ---------------------------------------------------------------------------
// Bases legales completas (transcripción del PDF oficial).
// Cada sección: { t: título, c: [párrafo | array-de-viñetas] }
// ---------------------------------------------------------------------------
const BASES_SECTIONS = [
  {
    t: '',
    c: [
      'La sociedad AF PICTURES PRODUCTIONS, S.L. (en lo sucesivo AF PICTURES) a través del CLUB AF convoca la I Edición del Concurso de Guiones de Cortometrajes de “Terror” (en adelante el “CONCURSO”).',
      'La presentación del guion de cortometraje (en adelante, el “GUION”) al CONCURSO implica necesariamente la aceptación previa, íntegra e incondicional de las siguientes bases por parte de los participantes en el mismo, así como de los criterios y decisiones que pueda establecer AF PICTURES a través del CLUB AF (las “Bases”), incluyendo, sin estar limitado a las que a continuación se señalan:',
    ],
  },
  {
    t: 'PRIMERO. — PARTICIPANTES',
    c: [
      'El CONCURSO va dirigido a estudiantes mayores de edad, residentes legales en España y que, actualmente estén cursando estudios de cine (o cualquier disciplina afín) grado en audiovisuales, módulo de formación profesional de sonido, entre otros, en alguna escuela de cine, universidad o centro de formación profesional de España o bien haber terminado sus estudios en el curso 2025-2026, (en adelante el “PARTICIPANTE”).',
      'No podrán presentarse al presente concurso, aquellas personas que mantengan directa o indirectamente, una relación (i) de parentesco hasta el segundo grado de consanguinidad o afinidad, o (ii) relación profesional, académica, personal o comercial; con personas involucradas en el proceso de selección, miembros del jurado o con cualquiera de las sociedades pertenecientes al grupo empresarial AF, sus directivos y/o colaboradores.',
      'Tampoco podrán participar en el concurso, los empleados del grupo de empresarial AF.',
    ],
  },
  {
    t: 'SEGUNDO. — CONCURSO',
    c: [
      'La participación en el CONCURSO será gratuita. El PARTICIPANTE de la presente convocatoria deberá ser el único y exclusivo autor y titular de todos los derechos de propiedad intelectual sobre el guion que presente al CONCURSO. En el caso de ser coautores, deberán ser titulares de todos los derechos conjuntamente.',
      'El PARTICIPANTE declara y garantiza, bajo su exclusiva responsabilidad, que el guion presentado: (i) constituye una obra original, inédita y resultado de su propia creación intelectual, respecto de la cual ostenta la plena titularidad y libre disposición de todos los derechos de propiedad intelectual necesarios para su participación en el presente concurso; (ii) no infringe ni vulnera derechos de propiedad intelectual, derechos de imagen, honor, intimidad, protección de datos, secretos empresariales ni cualesquiera otros derechos de terceros; (iii) no ha sido producido, publicado, explotado comercialmente, premiado, cedido, licenciado, opcionado ni se encuentra sujeto a cargas, gravámenes o derechos de terceros que limiten su libre explotación; (iv) no se encuentra participando simultáneamente en ningún otro concurso, convocatoria, laboratorio, proceso de adquisición, negociación de opción, cesión o cualquier otro procedimiento incompatible con las presentes Bases; (v) ha sido elaborado sin el uso de herramientas de inteligencia artificial generativa para la creación total o parcial, redacción, adaptación, reescritura o desarrollo sustancial de la obra o de cualquiera de sus elementos creativos esenciales, permitiéndose únicamente su utilización para funciones meramente auxiliares de corrección ortográfica, gramatical o de formato, siempre que ello no afecte a la autoría ni al carácter original de la obra; y (vi) se obliga a mantener plenamente indemne al grupo empresarial AF, a las sociedades de su grupo, a los miembros del jurado y a cualquier tercero designado por éstos frente a cualquier reclamación, acción, procedimiento, daño, perjuicio, coste, gasto o responsabilidad, incluidos los honorarios razonables de abogados y profesionales, que pudiera derivarse del incumplimiento de cualquiera de las anteriores declaraciones y garantías.',
      'Asimismo, AF PICTURES podrá excluir en cualquier momento del concurso al PARTICIPANTE respecto del cual existan indicios razonables de incumplimiento de las presentes declaraciones y garantías, sin perjuicio de las acciones legales que pudieran corresponder.',
    ],
  },
  {
    t: 'TERCERO. — PROYECTOS',
    c: [
      'Cada PARTICIPANTE, podrá: (i) enviar hasta tres (03) guiones a la convocatoria, (ii) el guion podrá ser presentado en idioma castellano o inglés; (iii) el género será ficción (no se aceptarán guiones de animaciones); (iv) la temática es “terror” y, (v) deberá desarrollarse en una sola localización, es decir, un hospital, una casa o una edificación abandonada.',
      'El guion podrá ser presentado por máximo dos (2) autores, en cuyo caso el premio se repartirá entre ellos en partes iguales.',
      'El guion deberá cumplir con las siguientes pautas:',
      [
        'La extensión del guion deberá de ser de 20 a 30 páginas (entre 35.000 y 60.000 caracteres), sin incluir la portada; y se estima una página de guion en formato estándar por minuto de obra audiovisual.',
        'Debe presentarse en formato profesional, siguiendo las convenciones del sector (letra “Courier” 12 o similar, interlineado doble).',
      ],
      'Al momento de la consignación del guion deberá adjuntar los siguientes documentos:',
      [
        'Memoria del guion (intenciones y bases creativas del proyecto).',
        'Resumen curricular del guionista(s).',
        'Comprobante o resguardo de la inscripción del guion en el Registro de la Propiedad intelectual o en la plataforma de registro de derechos de autor.',
        'Comprobante de estudios.',
        'Copia del DNI, NIE, o pasaporte.',
        'En el caso de participación colectiva (hasta un máximo de 2 autores), deberá consignar una “Declaración de conformidad” firmado por ambos guionistas en el cual se indica la conformidad de la inscripción en el concurso, y la designación de un participante como representante que será la persona encargada de presentar la candidatura a través de los medios habilitados.',
      ],
      'Y deberá completar el formulario disponible, indicando la siguiente información: nombre, apellidos, nacionalidad, fecha de nacimiento, edad, domicilio, localidad, código postal, país, género, centro de estudios, estudios, número de DNI/NIE, teléfono de contacto (móvil), correo electrónico, y título de proyecto, si son varios deberá realizar una entrega por cada guion.',
      'Los PARTICIPANTES en el concurso podrán enviar sus candidaturas, guiones y documentación, de la siguiente manera:',
      [
        'Acceder a la página web de “AF PICTURES” a través del siguiente link https://afpictures.com/',
        'Completar la información solicitada en el formulario.',
        'Adjuntar la documentación indicada, en formato PDF, PNG, JPG, tomando en consideración el tamaño máximo de cada archivo.',
        'Leer y aceptar las bases del CONCURSO.',
      ],
      'Cualquier duda nos podrás contactar a través de la dirección de correo electrónico afclub@afpictures.com.',
      'La presentación y recepción de los guiones será desde el día 22 de julio de 2026 y finalizará el 09 de septiembre 2026 a las 24:00 hrs, no aceptándose proyectos fuera del plazo. Se fija como fecha de resolución del concurso el día 16 de octubre de 2026.',
      'AF PICTURES bajo su propio criterio, podrá ampliar las fechas de presentación de los proyectos, previa comunicación pública a través de nuestros medios sociales, página web, correo electrónico.',
    ],
  },
  {
    t: 'CUARTO. — EXCLUSIÓN DE PROYECTOS',
    c: [
      'Quedarán excluidos del presente concurso y no serán presentados al jurado aquellos guiones que: (i) no adjunten los documentos señalados en la cláusula tercera, o aquellos que incumplan cualquiera de las Bases estipuladas; (ii) constituyan adaptaciones de obras preexistentes o respecto de las cuales el PARTICIPANTE no ostente la plena titularidad de los derechos necesarios para su presentación; (iii) hayan sido premiados en otros concursos, o que sean el resultado de algún programa o laboratorio de guiones o que se encuentren comprometidos con productoras, se encuentren sujetos a acuerdos de producción, cesión, licencia, opción de compra o cualquier otro compromiso que limite su libre disponibilidad; (iv) guiones que al momento de su presentación se encuentren pendiente de resolución de cualquier otro concurso similar de guiones o de proyectos de cortometrajes; (v) correspondan a proyectos de animación o documentales; (vi) aquellos que se encuentren bajo el amparo de acuerdos para su producción, incluyendo expresamente las opciones de compra del guion; (vii) hayan sido creados total o parcialmente mediante herramientas de inteligencia artificial generativa en contravención de lo dispuesto en las presentes Bases; (viii) infrinjan o existan indicios razonables de que puedan infringir derechos de propiedad intelectual o cualesquiera otros derechos de terceros; (ix) incumplan la extensión, formato o demás especificaciones técnicas establecidas en las presentes Bases; (x) hayan sido presentados fuera del plazo previsto; o (xi) cuando el PARTICIPANTE hubiera aportado información o documentación falsa, inexacta, incompleta o engañosa.',
    ],
  },
  {
    t: 'QUINTO. — USO DE IMAGEN Y DIFUSIÓN DEL CONCURSO',
    c: [
      'La participación en el presente CONCURSO implica la autorización expresa, gratuita y no exclusiva del PARTICIPANTE a favor de AF PICTURES PRODUCTIONS, S.L., sociedad mercantil constituida según las leyes del Reino de España, titular del CIF No. B44697076, con domicilio social en la Calle Alcalá, 126, Bajo, CP 28009, Madrid (en adelante “AF PICTURES”) para el uso de AF PICTURES, el Club AF y las restantes sociedades pertenecientes al grupo empresarial AF, para captar, reproducir, comunicar públicamente y difundir su nombre, apellidos, imagen, voz, biografía, trayectoria profesional, fotografías, centro de estudios, ciudad de residencia y cualquier otro dato estrictamente relacionado con su participación en el CONCURSO, con la única finalidad de promocionar el propio CONCURSO, comunicar sus resultados, anunciar a los finalistas y ganadores y difundir las actividades desarrolladas por AF relacionadas con esta iniciativa.',
      'Esta autorización comprenderá su utilización en cualquier medio o soporte, conocido o que pueda desarrollarse en el futuro, incluyendo, sin carácter limitativo, la página web de AF, redes sociales, newsletters, notas y comunicados de prensa, entrevistas, medios de comunicación, presentaciones corporativas, materiales promocionales, eventos presenciales o virtuales, plataformas audiovisuales y cualesquiera otros canales de comunicación internos o externos.',
      'En caso de resultar finalista o ganador, el PARTICIPANTE acepta participar, cuando razonablemente le sea solicitado, en entrevistas, sesiones fotográficas, grabaciones audiovisuales o actividades promocionales relacionadas con el CONCURSO, sin que ello genere derecho a remuneración adicional distinta del premio previsto en las presentes Bases.',
      'La presente autorización tendrá carácter mundial, por todo el plazo de protección legal de los derechos que resulten aplicables y no dará lugar al pago de contraprestación económica alguna, sin perjuicio de los derechos irrenunciables reconocidos por la legislación vigente.',
    ],
  },
  {
    t: 'SEXTO. — PREMIO Y SELECCIÓN DEL GANADOR',
    c: [
      'Solo será calificado como ganador un (1) solo guion, entregando al PARTICIPANTE, un premio cuyo importe asciende a la cantidad de TRES MIL EUROS (3.000,00€) brutos. En el caso que, el guion ganador sea de una autoría colectiva entre dos (2) autores, el citado importe será repartido entre los coautores en partes iguales. Al importe del premio se le deberá deducir el porcentaje de tributación correspondiente según la legislación española.',
      'El premio económico constituye la contraprestación única, total y suficiente por la cesión de derechos prevista en las presentes Bases, renunciando expresamente el PARTICIPANTE ganador a reclamar cualquier remuneración adicional derivada de dicha cesión, sin perjuicio de aquellos derechos irrenunciables que pudieran corresponderle conforme a la Ley de Propiedad Intelectual.',
    ],
  },
  {
    t: 'SÉPTIMO. — JURADO Y DELIBERACIÓN',
    c: [
      'La deliberación del CONCURSO recaerá sobre un jurado; que está constituido por el equipo de contenidos del grupo empresarial AF, pudiendo AF PICTURES, a su exclusiva discreción, incorporar a dicho Jurado profesionales internos o externos de reconocida experiencia en el desarrollo, producción, financiación, adquisición, distribución o evaluación de contenidos audiovisuales, así como cualquier otra personalidad de la industria cuya participación considere conveniente para garantizar la calidad y objetividad del proceso de selección. Los miembros del Jurado podrán abstenerse cuando exista un conflicto de interés.',
      'Las deliberaciones y votaciones del jurado serán secretas, y para la toma de la decisión se tomará en consideración los siguientes criterios de evaluación: (i) originalidad y creatividad de la propuesta; (ii) calidad narrativa y solidez de la estructura dramática; (iii) construcción de personajes; (iv) potencial cinematográfico; (v) adecuación al género de terror, y; (vi) potencial comercial y de explotación audiovisual.',
      'El jurado seleccionará un (1) ganador entre todos los PARTICIPANTES. Asimismo, AF PICTURES podrá declarar desierto el concurso total o parcialmente, no adjudicar el premio, seleccionar finalistas sin declarar ganador o no producir el proyecto ganador.',
      'El ganador del CONCURSO será contactado vía correo electrónico; y AF PICTURES se reservará el derecho de hacer público al ganador vía “mailing”, redes sociales y en la página web.',
      'La decisión del jurado será irrevocable e inapelable.',
      'Si el ganador no acepta el premio o no formaliza la cesión de derechos en el plazo de quince (15) días hábiles desde su notificación, AF PICTURES podrá dejar sin efecto la adjudicación y seleccionar otro proyecto.',
    ],
  },
  {
    t: 'OCTAVO. — CESIÓN DE DERECHOS',
    c: [
      'El PARTICIPANTE que resulte ganador del CONCURSO, autoriza a AF PICTURES a modificar, adaptar, combinar o desarrollar libremente el guion presentado por el ganador del CONCURSO, incluyendo la incorporación de nuevos guionistas, nuevas tramas o elementos adicionales necesarios en el caso de que se considere producir uno o varios potenciales proyectos audiovisuales.',
      'A los efectos de las presentes Bases, “proyectos audiovisuales” significará cualquier obra, producción, contenido, formato o explotación creativa basada, inspirada o derivada, total o parcialmente, del GUION o de cualquiera de sus elementos creativos, incluyendo, entre otros, personajes, tramas, argumentos, conceptos, diálogos, localizaciones y universos narrativos. Dicho término comprenderá igualmente cualquier adaptación, transformación, secuela, precuela, remake, reboot, spin-off, crossover, franquicia, obra derivada, explotación transmedia, producto derivado o cualquier otra creación o explotación, en cualquier formato, medio, soporte o tecnología, conocida en la actualidad o que pueda desarrollarse en el futuro.',
      'El PARTICIPANTE que resulte ganador del CONCURSO, cede a AF PICTURES de manera exclusiva, todos los derechos de Propiedad Intelectual susceptibles de ser cedidos sobre (i) el Guion, sus versiones, materiales de trabajo, incluido sin carácter limitativo, tramas, tratamientos, argumentos, situaciones, escaletas, dossieres, y los elementos incorporados a los mismos tales como, a título enunciativo y no limitativo: personajes, nombres y/o situaciones, incorporados o no a posibles o potenciales proyectos audiovisuales; (ii) la titularidad para representar, asesorar y gestionar a su exclusiva discreción, todas las actividades relacionadas con la venta, licenciamiento y desarrollo de potenciales o posibles proyectos audiovisuales derivados del Guion, y en especial, a título meramente enunciativo y no limitativo, los siguientes derechos:',
      [
        'DE DIVULGACIÓN, entendiéndose por tal el derecho de hacer accesible o poner a disposición del público por primera vez posibles o potenciales proyectos audiovisuales.',
        'DE REPRODUCCIÓN, tanto directa como indirecta, entendiéndose por tal la fijación de posibles o potenciales proyectos audiovisuales, tanto total como parcialmente, o la representación digital del mismo, tanto total como parcialmente, en un medio existente o por inventarse, que permita su comunicación y la obtención de copias de todo o parte de estas, incluyendo pero sin estar limitado a (i) la fijación de los posibles o potenciales proyectos audiovisuales en cualquier soporte analógico o digital, incluidos los denominados multimedia, patentados o inventados sin patentar (también conocidos, a título enunciativo, pero no limitativo, cómo DVD, Blue-ray, CD ROM, CDI, CD LASER e Internet), (ii) la fijación de potenciales proyectos audiovisuales en bases de datos de cualquier naturaleza, incluidos aquellas que permiten tanto la descarga para su fijación (downloading) como el simple visionado (streaming), así como la edición literaria y/o gráfica de potenciales proyectos audiovisuales.',
        'DE DISTRIBUCIÓN, entendiéndose por tal la puesta a disposición del público del original o copias de potenciales proyectos audiovisuales, o de la representación digital de los mismos, tanto total como parcialmente, mediante su venta, alquiler, préstamo o cualquier forma de transferencia temporal o definitiva de la posesión y/o propiedad de los derechos sobre potenciales proyectos audiovisuales, tanto para su comunicación pública o en el ámbito doméstico a través de la distribución videográfica mediante venta o alquiler en cualquier soporte, tales como video, DVD, Blue Ray o CD-ROM, a través de bases de datos de cualquier naturaleza, incluidos aquellos que permiten tanto la descarga para su fijación (downloading) como el simple visionado (Streaming), así como la edición literaria y/o gráfica de potenciales proyectos audiovisuales.',
        'DE COMUNICACIÓN PÚBLICA, entendiéndose por tal todo acto por el cual una pluralidad de personas pueda tener acceso a potenciales Proyectos Audiovisuales o a la representación digital de los mismos, tanto total como parcialmente, sin previa distribución de ejemplares, en cualquier modalidad establecida en el artículo 20 de la Ley de Propiedad Intelectual, incluyéndose entre los actos que integran el concepto, la emisión, transmisión y retransmisión por medio de la radiodifusión de cualquier tipo, la puesta a disposición del público (por procedimientos alámbricos o inalámbricos), el acceso a través de redes de comunicación analógicas y/o digitales y/o en lugares abiertos al público en general o de forma restringida; se incluye en este concepto tanto la comunicación pública de los posibles o potenciales Proyectos Audiovisuales, así como de los programas u obras en los que se incluyan, los demás sistemas de recuperación electrónica por cualquier procedimiento, incluyendo los sistemas de acceso condicionado, sean éstos en línea (on line) o fuera de ella (off line), así como los que permiten tanto la descarga para la subsiguiente fijación (downloading) como el simple visionado (streaming). Con carácter meramente enunciativo y no exhaustivo, se entienden incluidas en este apartado, la televisión analógica o digital, por cable, por satélite, televisión gratuita y de pago, o cualquier otra modalidad de emisión, transmisión o retransmisión (ya sea electrónica, tradicional, digital o analógica), cinematográfica, la representación escénica/teatral, videográfica, a través de merchandising y por cualquier otro medio de explotación, incluyendo (pero no limitado a) la explotación de los posibles o potenciales proyectos audiovisuales a través de pay per view (PPV), video on demand (VOD), near video on demand (NVOD), suscripción bajo demanda (SVOD), vídeo a la carta transaccional (TVOD), free video on demand (FVOD), video on demand soportado con publicidad (AdVOD) y, en general, a través de Internet, PC, Telefonía móvil, Smartphones, Ipad, EReaders, así como cualquier otro sistema de recuperación electrónica de los posibles o potenciales proyectos audiovisuales, existente o por ser inventado en el futuro.',
        'DE TRANSFORMACIÓN, entendiéndose por tal, en su forma más amplia y sin limitaciones, el derecho a la traducción, adaptación y cualquier otra modificación en su forma o fondo de la que se derive una obra diferente de posibles o potenciales Proyectos. Igualmente, se entienden incluidos en la cesión del derecho de transformación, los cambios de soporte y/o sobre el original de las Obras y/o de posibles o potenciales Proyectos, así como el derecho DE DOBLAJE Y SUBTITULADO a cualquier idioma y/o lengua. Asimismo, se incluyen las obras derivadas incluyendo sin estar limitado a remakes, spin off, secuelas, precuelas y/o adaptaciones de las Obras y/o posibles o potenciales Proyectos a formatos existentes o que sean inventados en el futuro.',
      ],
      'Los derechos de explotación del GUION, las Obras y/o de los posibles o potenciales Proyectos son cedidos mediante las presentes bases del concurso, con carácter exclusivo, en cualquier forma y formato, presente o futuro. Esta cesión incluye, entre otros, la producción de posibles o potenciales proyectos audiovisuales, así como su comercialización, distribución, difusión o cualquier otra modalidad de explotación conexa o derivada. La cesión se otorga para todos los idiomas, territorios del mundo, a perpetuidad o hasta el paso de los derechos al dominio público, teniendo como únicos límites aquellos establecidos por la Ley, con plena facultad para que “AF PICTURES” ceda, licencie o transmita estos derechos, gratuita u onerosa. “AF PICTURES” podrá explotar el Guion, los posibles o potenciales Proyectos mediante merchandising, productos derivados o incorporándolos a otras obras o producciones audiovisuales. Esta explotación podrá realizarse de forma aislada o integrada en otras actividades, siempre con el objetivo de promover y maximizar el éxito comercial de los posibles o potenciales Proyectos y de otros productos o servicios relacionados, del modo más amplio posible, abarcando usos implícitos o derivados razonablemente del presente Contrato, incluso si no están expresamente mencionados o si no fueren conocidos o existieren al momento de la firma del presente Contrato. Las Partes garantizan que “AF PICTURES” ostenta todos los derechos de Propiedad Intelectual sobre el Guion, de los posibles o potenciales Proyectos y cualquier material aportado por el PARTICIPANTE, incluyendo obras derivadas.',
    ],
  },
  {
    t: 'NOVENO. — PROTECCIÓN DE DATOS',
    c: [
      'En cumplimiento del Reglamento (UE) 2016/679 del Parlamento Europeo y del Consejo de 27 de abril de 2016 (en adelante, RGPD), “AF PICTURES”, como entidad corresponsable del tratamiento, informa a los participantes de la I edición del Concurso de guiones de cortometrajes de Terror “Summer Screams de Club AF”, que los datos facilitados para su participación en el mismo serán recogidos y tratados, en todo momento, con la finalidad de gestionar su participación en el concurso, seleccionar a los finalistas del mismo, gestionar la realización del concurso y a participar, si lo desean, en entrevistas que podrán publicarse en sus redes sociales, páginas webs y cualquier otro medio social utilizado para la difusión del concurso. La base que legitima el tratamiento de sus datos personales es la existencia de una relación contractual manifestada mediante la aceptación de las bases y la presentación al concurso. Los datos serán conservados durante el plazo estrictamente necesario para cumplir con los fines mencionados con anterioridad. Pasado este tiempo, se conservarán debidamente bloqueados a los efectos de determinar las posibles responsabilidades que se pudieran derivar de dicha finalidad y del tratamiento de los datos.',
      '“AF PICTURES” como corresponsable del tratamiento, procederá a tratar los datos de manera lícita, leal, transparente, adecuada, pertinente, limitada, exacta y actualizada y no lo cederá a terceros salvo obligación legal y en cumplimiento de las propias finalidades del concurso. Siempre de acuerdo con la Decisión 2021/914, relativa al conjunto de cláusulas para las transferencias internacionales de datos a terceros países, las partes pueden realizar transferencias internacionales de datos a entidades ubicadas en países fuera del Espacio Económico Europeo que garanticen un nivel de protección equivalente al del RGPD. Adicionalmente, las partes también pueden contratar servicios de proveedores tecnológicos ubicados en países fuera del Espacio Económico Europeo. En el supuesto de contratarse dichos servicios, tal contratación cumpliría con todos los requisitos establecidos por la normativa de protección de datos, con aplicación a la transferencia de sus datos personales de las garantías y salvaguardas necesarias para preservar su privacidad.',
      'Asimismo, de manera excepcional, sus datos personales también pueden ser transferidos a entidades ubicadas fuera del Espacio Económico Europeo cuando sea necesario por razones importantes de interés público, conforme a lo establecido en la normativa española y europea a la que las partes estén sujetas por razón de su actividad. Podrá ejercer los derechos de acceso, rectificación, limitación de tratamiento, supresión, portabilidad y oposición al tratamiento de sus datos de carácter personal, así como la revocación del consentimiento prestado para el tratamiento de los mismos, dirigiendo su petición a los corresponsables del tratamiento:',
      [
        'AF PICTURES PRODUCTIONS S.L.: a la dirección electrónica administracion@frankariza.com o por correo postal, a la siguiente dirección: Calle Alcalá 126, 28009, Madrid (Madrid).',
      ],
      'En ambos casos deberá adjuntar copia de su DNI, pasaporte o documento equivalente que lo identifique, e indicar el motivo de ejercicio del derecho.',
      'También puede presentar una reclamación a las direcciones indicadas anteriormente o ante la Agencia Española de Protección de Datos (www.agpd.es).',
    ],
  },
  {
    t: 'DÉCIMO. — MISCELÁNEOS',
    c: [
      [
        'AF PICTURES no asume obligación alguna con el PARTICIPANTE ganador del CONCURSO de desarrollar, financiar, producir, distribuir o explotar el GUION.',
        'AF podrá modificar las presentes Bases por causas organizativas, técnicas, legales o de fuerza mayor, siempre que ello no perjudique los derechos ya adquiridos por los participantes, publicando dichas modificaciones por los mismos medios de difusión.',
        'AF podrá solicitar en cualquier momento documentación adicional destinada a verificar la identidad del participante, la autoría del guion o el cumplimiento de cualquiera de los requisitos establecidos en las presentes Bases. Asimismo, AF podrá requerir al participante borradores, versiones de trabajo, archivos editables o cualquier otra documentación que permita verificar el proceso de creación del guion.',
        'AF no será responsable por cancelaciones, retrasos, suspensión o modificaciones del concurso por causas de fuerza mayor, caso fortuito o circunstancias ajenas a su control.',
        'El ganador autoriza expresamente a AF PICTURES a realizar las modificaciones, adaptaciones, cortes, ampliaciones, traducciones, incorporaciones de nuevos personajes, guionistas, escenas o cualesquiera otras transformaciones necesarias para el eventual desarrollo audiovisual del proyecto.',
        'Los participantes se comprometen a no divulgar comunicaciones internas del Jurado, deliberaciones, evaluaciones, puntuaciones o documentación del proceso de selección.',
        'AF PICTURES o cualquiera de sus filiales o cualquiera de sus empresas relacionadas podrá estar desarrollando de forma independiente conceptos y negocios similares a alguno de los guiones presentados por los PARTICIPANTES, en cualquier parte del mundo, y los PARTICIPANTES no tendrán derecho a impedir que AF PICTURES o cualquiera de sus filiales o cualquiera de sus empresas relacionadas exploten cualquier idea, formato o historia similar o idéntica que haya sido desarrollada de manera independiente por AF PICTURES o cualquiera de sus filiales o cualquiera de sus empresas relacionadas, o presentada a AF PICTURES o cualquiera de sus filiales o cualquiera de sus empresas relacionadas por un tercero.',
        'Los PARTICIPANTES del CONCURSO, no tendrán derecho a percibir remuneración ni participación alguna en dichos proyectos, ni a alegar que dicha explotación constituye un incumplimiento de los derechos de los PARTICIPANTES sobre su candidatura o presentación al CONCURSO.',
        'AF PICTURES o cualquiera de sus filiales o cualquiera de sus empresas relacionadas aceptan que no utilizará deliberadamente la propuesta de los PARTICIPANTES, salvo que AF PICTURES o cualquiera de sus filiales o cualquiera de sus empresas relacionadas suscriban un acuerdo escrito independiente relativo a dicho uso.',
      ],
    ],
  },
  {
    t: 'DECIMOPRIMERO. — JURISDICCIÓN Y LEY APLICABLE',
    c: [
      'Las presentes Bases se regirán por la legislación española. Para cuantas cuestiones litigiosas pudieran derivarse de su interpretación o ejecución, las partes se someten expresamente a los Juzgados y Tribunales de Madrid, con renuncia a cualquier otro fuero que pudiera corresponderles, salvo que la normativa imperativa disponga otra cosa.',
      'Para información adicional sobre la I EDICIÓN DEL CONCURSO DE GUIONES DE CORTOMETRAJES DE TERROR SUMMER SCREAMS DE CLUB AF, podrás acceder al siguiente link https://afpictures.com/ o escribir al correo electrónico afclub@afpictures.com.',
    ],
  },
];

function ConcursoGuiones(props) {

  const featuredImage = 'https://discover.af-pictures.com/storage/app/media/upload-img/Poster%20Horizontal%20Summer%20Screams.png';

  const organizationSchema = generateOrganizationSchema();
  const websiteSchema = generateWebsiteSchema();
  const combinedSchema = [organizationSchema, websiteSchema];

  const formRef = useRef(null);
  const mountedAt = useRef(Date.now());
  const [dosGuionistas, setDosGuionistas] = useState(false);
  const [showBases, setShowBases] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [basesRead, setBasesRead] = useState(false);
  const [basesAccepted, setBasesAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', text: '' });
  const [missingFields, setMissingFields] = useState([]);
  const [filesTotalMb, setFilesTotalMb] = useState(0);

  // Bloquear el scroll de la página mientras haya un modal abierto
  useEffect(() => {
    document.body.style.overflow = showBases || showPoster ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showBases, showPoster]);

  const inputClass = 'w-full px-4 py-2.5 text-white bg-zinc-800 border border-white/[0.12] rounded-md focus:outline-none focus:ring-2 focus:ring-[#735707] transition-colors';
  const fileClass = 'block w-full text-sm text-gray-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border file:border-solid file:border-white/[0.12] file:text-sm file:font-semibold file:bg-[#2A2624] file:text-white hover:file:bg-zinc-800 file:cursor-pointer';

  const isFieldEmpty = (el) => {
    if (el.type === 'file') return el.files.length === 0;
    if (el.tagName === 'SELECT') return el.value.trim() === '';
    if (el.type === 'checkbox') return !el.checked;
    return el.value.trim() === '';
  };

  const computeFilesTotal = () => {
    if (!formRef.current) return 0;
    const inputs = Array.from(formRef.current.querySelectorAll('input[type="file"]'));
    return inputs.reduce(
      (sum, el) => sum + Array.from(el.files || []).reduce((a, f) => a + f.size, 0),
      0
    );
  };

  const renderGuionistaFields = (suffix, required) =>
    GUIONISTA_FIELDS.map((f) => (
      <div key={`${f.name}_${suffix}`}>
        <label className="block text-sm font-medium text-gray-100 mb-2">
          {f.label} {required && <span className="text-red-500">*</span>}
        </label>
        {f.type === 'select' ? (
          <select name={`${f.name}_${suffix}`} required={required} data-label={f.label} className={inputClass}>
            <option value="">Seleccionar...</option>
            {GENERO_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        ) : (
          <input type={f.type} name={`${f.name}_${suffix}`} required={required} data-label={f.label} className={inputClass} />
        )}
      </div>
    ));

  const renderFile = (name, label, required, accept, hint) => (
    <div>
      <label className="block text-sm font-medium text-gray-100 mb-2">
        {label}{' '}
        <span className="text-gray-500 text-xs">({accept.includes('jpg') ? 'PDF o JPG' : 'PDF'})</span>{' '}
        {required && <span className="text-red-500">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <input type="file" name={name} required={required} accept={accept} data-label={label} className={fileClass} />
    </div>
  );

  const handleFieldFix = (e) => {
    const el = e.target;
    if (el.hasAttribute && el.hasAttribute('required') && !isFieldEmpty(el)) {
      el.classList.remove('ring-2', 'ring-red-500');
    }
    if (el.type === 'file') {
      setFilesTotalMb(computeFilesTotal() / 1024 / 1024);
    }
  };

  // Scroll dentro del modal: al llegar (casi) al final se desbloquea el botón de aceptar
  const handleBasesScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      setBasesRead(true);
    }
  };

  const acceptBases = () => {
    setBasesAccepted(true);
    setShowBases(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = formRef.current;
    setSubmitStatus({ type: '', text: '' });
    setMissingFields([]);

    if (!basesAccepted) {
      setSubmitStatus({ type: 'error', text: 'Debes leer y aceptar las bases del concurso antes de enviar la inscripción.' });
      return;
    }

    const requiredFields = Array.from(form.querySelectorAll('[required]')).filter((el) => el.offsetParent !== null);
    requiredFields.forEach((el) => el.classList.remove('ring-2', 'ring-red-500'));
    const missing = requiredFields.filter(isFieldEmpty);
    if (missing.length > 0) {
      missing.forEach((el) => el.classList.add('ring-2', 'ring-red-500'));
      setMissingFields(missing.map((el) => el.getAttribute('data-label') || el.name));
      missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Límite total de adjuntos
    const totalBytes = computeFilesTotal();
    if (totalBytes > MAX_TOTAL_MB * 1024 * 1024) {
      setSubmitStatus({
        type: 'error',
        text:
          'Los documentos adjuntos suman ' +
          (totalBytes / 1024 / 1024).toFixed(1) +
          ' MB y el máximo total permitido es ' +
          MAX_TOTAL_MB +
          ' MB. Reduce el tamaño de los archivos (por ejemplo, comprimiendo los PDF) e inténtalo de nuevo.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(PF_ENDPOINT, {
        method: 'POST',
        body: new FormData(form),
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'No se pudo enviar la inscripción.');
      }
      setSubmitStatus({
        type: 'success',
        text: data.message || 'Hemos recibido tu inscripción. Revisa tu correo por si necesitamos contactarte.',
      });
      form.reset();
      setDosGuionistas(false);
      setBasesAccepted(false);
      setBasesRead(false);
      setShowBases(false);
      setFilesTotalMb(0);
    } catch (err) {
      setSubmitStatus({
        type: 'error',
        text:
          err && err.message
            ? err.message
            : 'No se pudo enviar la inscripción. Inténtalo de nuevo en unos minutos o escríbenos a afclub@afpictures.com.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title='Concurso de Guiones - Summer Screams'
        description='En AF creemos en el talento emergente. Lanzamos un concurso dirigido a estudiantes de cine y audiovisuales para presentar sus mejores guiones de cortometraje de terror. Premio de 3.000€.'
        keywords='concurso de guiones, cortometraje de terror, guionistas, AF Pictures, concurso cine, talento emergente'
        url='https://afpictures.com/concurso-guiones'
        image='https://afpictures.com/img/af-pictures-og.jpg'
        structuredData={combinedSchema}
      />

      <div style={{ backgroundImage: 'url(' + featuredImage + ')',backgroundPosition:'center 38%', backgroundSize:'cover'}} className="relative background-cover items-center h-[40vh]">
                <h1 style={{fontSize:'200%'}} className="text-center w-full pt-[20vh]  font-semibold text-white">CONCURSO DE GUIONES</h1>
    </div>
      <div className={"w-full overflow-x-hidden bg-zinc-900 text-white " + props.transition}>
        <div className="max-w-[820px] mx-auto px-4 md:px-8 py-12 lg:py-20">

          {/* Header */}
          <div className="text-center mb-10">
            <p className="font-mono text-2xl font-bold text-center text-red-800">AF PICTURES PRESENTA</p>
            <img
              src={IMG.logo}
              alt="Summer Screams"
              className="mx-auto w-full md:w-96 max-w-full mb-4"
            />
            <h2 className="text-2xl md:text-3xl font-semibold">Concurso de Guiones</h2>
          </div>

          {/* Intro */}
          <div className="space-y-6 text-lg md:text-xl text-gray-200 leading-relaxed text-center mb-12">
            <p>
              En AF creemos en el talento emergente y queremos ayudar a descubrir a la próxima
              generación de guionistas. Por eso lanzamos un concurso dirigido a estudiantes de cine
              y audiovisuales, invitándoles a presentar sus mejores guiones de cortometraje de terror.
            </p>
            <p>
              El terror se ha consolidado como uno de los géneros favoritos del público joven y una
              de las grandes puertas de entrada para nuevos creadores. Buscamos historias originales,
              capaces de sorprender, inquietar y dejar huella, con el objetivo de encontrar nuevos
              talentos y dar visibilidad a las voces que marcarán el futuro del género.
            </p>
            <p>
              Con esta iniciativa queremos impulsar el inicio de la carrera de nuevos guionistas,
              ofreciéndoles la oportunidad de desarrollar su talento con el respaldo y la experiencia
              de AF, una de las productoras españolas con mayor proyección y crecimiento en el mercado
              internacional. El proyecto seleccionado podrá convertirse en el punto de partida de una
              futura producción audiovisual.
            </p>
          </div>

          {/* Stills de películas AF */}
          {/* Se ensancha más allá del contenedor de 820px centrándose sobre el eje de la página. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-12 lg:w-[min(1120px,100vw-4rem)] lg:ml-[50%] lg:-translate-x-1/2">
            {[
              { src: IMG.vestido, title: 'EL VESTIDO', genre: 'Terror psicológico', url: '/catalogo/el-vestido' },
              { src: IMG.silla, title: 'LA SILLA', genre: 'Thriller de terror', url: '/catalogo/la-silla' },
            ].map((film) => (
              <a
                key={film.title}
                href={film.url}
                className="relative aspect-[3/4] rounded-lg overflow-hidden group border border-white/[0.12] block cursor-pointer"
              >
                <img
                  src={film.src}
                  alt={'Fotograma de ' + film.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover brightness-[0.55] transform transition-all duration-700 group-hover:scale-105 group-hover:brightness-75"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40 pointer-events-none"></div>
                <div className="absolute inset-x-0 bottom-8 md:bottom-10 flex flex-col items-center gap-3 px-4 text-center">
                  <span className="border border-red-700 text-red-600 text-[10px] md:text-xs font-semibold uppercase tracking-[0.25em] px-3 py-1">
                    {film.genre}
                  </span>
                  <p className="text-white font-bold uppercase text-3xl md:text-5xl leading-none">
                    {film.title}
                  </p>
                </div>
              </a>
            ))}
          </div>

          {/* Prize highlight */}
          <div className="border-t border-b border-white/40 py-8 text-center mt-24 mb-5">
            <h3 className="text-2xl md:text-3xl font-bold">
              El ganador del concurso será premiado con
              <br />
              un premio de 3.000€ en metálico
            </h3>
          </div>
          <p className="text-center italic text-gray-400 mb-16">
            Más abajo encontrarás toda la información sobre el concurso
          </p>

          {/* En qué consiste (+ póster) */}
          {/* Se ensancha más allá del contenedor de 820px centrándose sobre el eje de la página. */}
          <section className="mb-14 grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-10 md:gap-14 lg:w-[min(1120px,100vw-4rem)] lg:ml-[50%] lg:-translate-x-1/2">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold mb-8">En qué consiste</h3>
              <ul className="space-y-5 text-gray-300 leading-relaxed">
                <Bullet>
                  Buscamos guiones de cortometrajes de terror de entre <strong>20-30 minutos</strong>.
                  Toda la historia deberá ocurrir en una de estas tres posibles localizaciones: un{' '}
                  <strong>HOSPITAL</strong>, una <strong>CASA</strong> o un{' '}
                  <strong>EDIFICIO ABANDONADO</strong>.
                </Bullet>
                <Bullet>Deben ser exclusivamente de ficción y no se considerarán cortos de animación o documental.</Bullet>
                <Bullet>Los guiones deben ser en castellano o en inglés.</Bullet>
                <Bullet>
                  Los participantes deben cursar actualmente estudios de cine o de audiovisual (de
                  cualquier disciplina) en alguna escuela de cine/universidad/centro de formación
                  profesional del país o bien haber terminado sus estudios en el curso 2025-2026.
                </Bullet>
                <Bullet>
                  El guion puede ser presentado por un máximo de dos autores, en este caso el premio
                  será repartido entre ambos a partes iguales.
                </Bullet>
                <Bullet>Los autores deben residir en España y ser personas físicas mayores de edad.</Bullet>
                <Bullet>
                  Las obras deben ser originales e inéditas, esto es, obras no producidas ni explotadas
                  de cualquier otra forma. Asimismo, tampoco podrán presentarse guiones que en el momento
                  de su envío a este concurso estén pendientes de la resolución de cualquier otro concurso
                  similar o en un laboratorio de desarrollo. Tampoco guiones que hayan sido premiados en
                  concursos previos.
                </Bullet>
                <Bullet>
                  El/La participante deberá ser titular único de todos los derechos de propiedad
                  intelectual sobre el guion que se presente al concurso. En caso de ser coautores,
                  deberán ser titulares de todos los derechos conjuntamente.
                </Bullet>
                <Bullet>
                  Cada participante podrá enviar un <strong>máximo de 3 guiones a la convocatoria</strong>.
                  Únicamente se premiará 1 de los guiones presentados.
                </Bullet>
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setShowPoster(true)}
              aria-label="Ampliar cartel del concurso"
              className="group relative w-full h-full overflow-hidden cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-slate-50 rounded-lg shadow-2xl border border-white/[0.12]"
            >
              <img
                src={IMG.poster}
                alt="Cartel del concurso Summer Screams"
                loading="lazy"
                className="w-full h-full object-cover rounded-lg transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <span className="absolute bottom-2 right-2 flex items-center justify-center w-8 h-8 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </span>
            </button>
          </section>

          {/* Titulo rojo del concurso */}
          <div className="my-12 lg:my-20 lg:w-[min(1120px,100vw-2rem)] lg:ml-[50%] lg:-translate-x-1/2">
          <img
              src={IMG.titulo}
              alt="Titulo del concurso Summer Screams"
              className="w-full"
            />          
          </div>

          {/* Documentación requerida */}
          <section className="mb-14 border-t border-white/40 pt-14">
            <h3 className="text-2xl font-bold text-center mb-6">Documentación requerida</h3>
            <ol className="list-decimal pl-6 space-y-4 text-gray-200 leading-relaxed">
              <li>
                Guion entre 20 y 30 páginas, sin incluir portada.
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Se estima una página de guion en formato estándar por minuto de obra audiovisual.</li>
                  <li>
                    Se debe presentar en formato profesional, siguiendo las convenciones de la
                    industria; <strong>Tipografía: Courier, Tamaño: 12 o similar, Interlineado doble.</strong>
                  </li>
                </ul>
              </li>
              <li>Memoria de guion donde se incluya las intenciones y bases creativas del proyecto.</li>
              <li>CV del guionista/s.</li>
              <li>Comprobante de estudios.</li>
              <li>
                Comprobante o resguardo de la inscripción del guion en el Registro de la Propiedad
                intelectual o en plataformas de registro de derechos de autor.
              </li>
              <li>Copia del DNI, NIE o pasaporte.</li>
              <li>
                En el caso de co-autoría: <strong>DECLARACIÓN DE CONFORMIDAD</strong> firmado por todas
                las partes para garantizar el acuerdo de la inscripción en la presente convocatoria.
              </li>
            </ol>
            <p className="text-center text-gray-300 mt-6">
              En caso de no presentar alguno de los documentos referidos, o que los mismos no se ajusten
              a lo estipulado, el proyecto será automáticamente descalificado.
            </p>
          </section>

          {/* Premio */}
          <section className="mb-14 border-t border-white/40 pt-14 text-center">
            <h3 className="text-2xl font-bold mb-4">Premio</h3>
            <p className="text-gray-200 leading-relaxed">
              Premio en metálico: <strong>premio de 3.000 euros</strong>. En caso de co-autoría será
              dividido a partes iguales, cuyas cuantías serán sometidas a la tributación que marque la
              legislación vigente.
            </p>
          </section>

          {/* Jurado */}
          <section className="mb-14 border-t border-white/40 pt-14 text-center">
            <h3 className="text-2xl font-bold mb-4">El jurado del concurso</h3>
            <div className="space-y-3 text-gray-200 leading-relaxed">
              <p>
                Un jurado formado íntegramente por el equipo de contenidos de la productora será
                encargado de elegir el proyecto ganador.
              </p>
              <p>Las deliberaciones y votaciones del jurado serán confidenciales.</p>
              <p>La elección del guion seleccionado, así como el ganador, tendrá carácter inapelable.</p>
            </div>
          </section>

          {/* Plazo y resolución */}
          <section className="mb-16 border-t border-white/40 pt-14 text-center">
            <h3 className="text-2xl font-bold mb-4">Plazo y resolución</h3>
            <div className="space-y-3 text-gray-200 leading-relaxed">
              <p>
                El plazo para el envío de guiones será desde el{' '}
                <strong>22 de julio hasta el 9 de septiembre</strong>. Ambos días hábiles.
              </p>
              <p>
                El/La ganador del concurso será contactado vía mail el <strong>16 de octubre</strong>.
              </p>
              <p className="font-bold underline">No se aceptarán guiones fuera de plazo.</p>
            </div>
          </section>

          {/* Formulario */}
          <section id="formulario" className="relative border-t border-white/40 pt-14">
            {/* Salpicaduras decorativas: sangran hacia los lados fuera del contenedor.
                El recorte lo hace el overflow-x-hidden del envoltorio de la página. */}
            <img
              src={IMG.salpicaduraIzq}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="hidden lg:block pointer-events-none select-none absolute -top-32 right-[calc(50%+360px)] w-[420px] max-w-none"
            />
            <img
              src={IMG.salpicaduraDer}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="hidden lg:block pointer-events-none select-none absolute top-36 left-[calc(50%+385px)] w-[420px] max-w-none"
            />

            <div className="relative text-center mb-8">
              <h3 className="text-3xl font-semibold mb-2">Inscripción</h3>
              <p className="italic text-gray-400">Imprescindible leer y aceptar las bases completas</p>
            </div>

            <div className="relative w-[calc(100%+20px)] -ml-[10px] rounded-lg border border-white/10 p-4 md:p-8">
              {submitStatus.type === 'success' ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-green-900 border border-green-700">
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-2">Inscripción enviada</h3>
                  <p className="text-green-200 max-w-md mx-auto">{submitStatus.text}</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-white mb-6 text-center">
                    <span className="text-red-500">*</span> indica campo obligatorio
                  </p>

                  {submitStatus.type === 'error' && (
                    <div className="mb-6 rounded-md border border-red-800 bg-red-950 text-red-200 text-sm p-4">
                      {submitStatus.text}
                    </div>
                  )}
                  {missingFields.length > 0 && (
                    <div className="mb-6 rounded-md border border-red-800 bg-red-950 text-red-200 text-sm p-4">
                      Faltan campos obligatorios: {missingFields.join(', ')}
                    </div>
                  )}

                  <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    onInput={handleFieldFix}
                    onChange={handleFieldFix}
                    className="space-y-6 text-white"
                    noValidate
                  >
                    <input type="text" name="web" tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
                    <input type="hidden" name="form_ts" defaultValue={mountedAt.current} />
                    {basesAccepted && <input type="hidden" name="bases_aceptadas" value="1" />}

                    <div className="pt-2">
                      <h3 className="text-xl font-semibold text-white mb-5 border-b border-white/15 pb-3">Datos del guionista 1</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderGuionistaFields('1', true)}
                      </div>
                    </div>

                    <label className="flex items-center space-x-3 cursor-pointer pt-2">
                      <input
                        type="checkbox"
                        name="dos_guionistas"
                        value="1"
                        checked={dosGuionistas}
                        onChange={(e) => setDosGuionistas(e.target.checked)}
                        className="w-4 h-4 accent-[#735707] bg-zinc-950 border-gray-600 rounded focus:ring-[#735707] focus:ring-2"
                      />
                      <span className="text-sm text-gray-100">Hay un segundo guionista</span>
                    </label>

                    {dosGuionistas && (
                      <div className="pt-2">
                        <h3 className="text-xl font-semibold text-white mb-5 border-b border-white/15 pb-3">Datos del guionista 2</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {renderGuionistaFields('2', true)}
                        </div>
                      </div>
                    )}

                    <div className="pt-2">
                      <h3 className="text-xl font-semibold text-white mb-5 border-b border-white/15 pb-3">Datos del proyecto</h3>
                      <label className="block text-sm font-medium text-gray-100 mb-2">
                        Título del proyecto <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="titulo_proyecto" required data-label="Título del proyecto" className={inputClass} />
                      <p className="mt-1 text-xs text-gray-500">Si son varios, separar los títulos con un punto y coma (;)</p>
                    </div>

                    <div className="pt-2">
                      <h3 className="text-xl font-semibold text-white mb-1 border-b border-white/15 pb-3">Documentación a adjuntar</h3>
                      <p className="text-xs text-gray-400 mb-4">
                        El conjunto de la inscripción no puede superar los <strong>{MAX_TOTAL_MB} MB</strong>.
                      </p>
                      <div className="space-y-4">
                        {renderFile('file_dni_1', 'Copia DNI o pasaporte — guionista 1', true, '.pdf,.jpg,.jpeg')}
                        {dosGuionistas && renderFile('file_dni_2', 'Copia DNI o pasaporte — guionista 2', true, '.pdf,.jpg,.jpeg')}
                        {renderFile('file_estudios_1', 'Comprobante de estudios — guionista 1', true, '.pdf,.jpg,.jpeg')}
                        {dosGuionistas && renderFile('file_estudios_2', 'Comprobante de estudios — guionista 2', true, '.pdf,.jpg,.jpeg')}
                        {renderFile('file_guion_1', 'Guion 1', true, '.pdf')}
                        {renderFile('file_guion_2', 'Guion 2 (opcional)', false, '.pdf')}
                        {renderFile('file_guion_3', 'Guion 3 (opcional)', false, '.pdf')}
                        {renderFile('file_memoria', 'Memoria (intenciones y bases creativas del proyecto)', true, '.pdf')}
                        {renderFile('file_cv_1', 'CV del guionista 1', true, '.pdf')}
                        {dosGuionistas && renderFile('file_cv_2', 'CV del guionista 2', true, '.pdf')}
                        {renderFile('file_registro', 'Comprobante de inscripción en el Registro de la Propiedad Intelectual', true, '.pdf')}
                        {dosGuionistas &&
                          renderFile(
                            'file_declaracion',
                            'Declaración de conformidad (firmada por ambos guionistas)',
                            true,
                            '.pdf',
                            'Obligatorio en caso de participación colectiva, indicando el representante designado.'
                          )}
                      </div>
                      {filesTotalMb > 0 && (
                        <p className={'mt-4 text-xs ' + (filesTotalMb > MAX_TOTAL_MB ? 'text-red-400 font-semibold' : 'text-gray-400')}>
                          Total adjuntado: {filesTotalMb.toFixed(1)} MB de {MAX_TOTAL_MB} MB permitidos
                          {filesTotalMb > MAX_TOTAL_MB ? ' — supera el máximo, reduce el tamaño de los archivos.' : '.'}
                        </p>
                      )}
                    </div>

                    {/* Bases del concurso: aceptar y enviar solo aparecen tras leerlas en el modal */}
                    <div className="pt-4 border-t border-gray-800">
                      {basesAccepted ? (
                        <>
                          <h3 className="text-lg font-semibold text-white mb-2">Bases del concurso</h3>
                          <div className="fadeIn flex items-start space-x-3 rounded-md border border-green-800 bg-green-950/40 p-4">
                            <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <div>
                              <p className="text-sm text-green-200">
                                Has leído y aceptado las bases legales del concurso.
                              </p>
                              <button
                                type="button"
                                onClick={() => setShowBases(true)}
                                className="text-xs text-gray-400 underline hover:text-white mt-1"
                              >
                                Volver a leer las bases
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <h3 className="text-xl md:text-2xl font-bold text-red-800 text-center mb-3">
                            Último paso: lee las bases legales del concurso
                          </h3>
                          <p className="text-sm text-gray-300 mb-4 text-center">
                            Para poder enviar tu inscripción es imprescindible leer las bases legales
                            completas hasta el final y aceptarlas.
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowBases(true)}
                            className="flex w-full items-center justify-center gap-2 mb-2 px-6 py-3 rounded-md bg-[#A7251B] uppercase text-white font-medium hover:opacity-90 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            Leer las bases legales del concurso
                          </button>
                          <p className="text-xs text-gray-500 text-center">
                            Se abrirán en una ventana sobre esta misma página.
                          </p>
                        </>
                      )}
                    </div>

                    {basesAccepted && (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="fadeIn w-full bg-[#735707] uppercase text-white py-3 px-6 rounded-md font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-[#735707] transition-colors"
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar inscripción'}
                      </button>
                    )}
                  </form>
                </>
              )}
            </div>

            <div className="relative text-center mt-8 mb-8">
              <p className="italic text-gray-200">Si tienes cualquier tipo de duda o consulta, por favor escríbenos a <a href="mailto:afclub@afpictures.com" className='text-red-600'>afclub@afpictures.com</a>.</p>
            </div>

            {/* Logo del concurso al pie */}
            <div className="text-center pt-12 flex flex-row md:flex-row items-center justify-center">
              <img
                src={IMG.logoAF}
                alt="Logo de AF Pictures"
                loading="lazy"
                className="mx-auto w-40 md:w-48 opacity-90"
              />
              <img
                src={IMG.logo}
                alt="Summer Screams — Concurso de Guiones del Club AF"
                loading="lazy"
                className="mx-auto w-40 md:w-48 opacity-90"
              />
            </div>
          </section>

        </div>
      </div>

      {/* Lightbox del cartel */}
      {showPoster && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Cartel del concurso ampliado"
          onClick={() => setShowPoster(false)}
        >
          <button
            type="button"
            onClick={() => setShowPoster(false)}
            aria-label="Cerrar"
            className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors z-10"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={IMG.poster}
            alt="Cartel del concurso Summer Screams"
            className="max-h-[90vh] max-w-full w-auto rounded-lg shadow-2xl cursor-zoom-out"
          />
        </div>
      )}

      {/* Modal de bases legales */}
      {showBases && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center p-3 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Bases legales del concurso"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowBases(false)}></div>

          <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col bg-zinc-900 border border-white/20 rounded-lg shadow-2xl overflow-hidden">
            {/* Cabecera del modal */}
            <div className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-white/10 bg-zinc-950">
              <div>
                <h3 className="text-lg md:text-xl font-bold text-white leading-tight">
                  Bases legales del concurso
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  I Edición del Concurso de Guiones de Cortometrajes de Terror “Summer Screams de Club AF”
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBases(false)}
                aria-label="Cerrar"
                className="ml-4 text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido con scroll obligatorio */}
            <div onScroll={handleBasesScroll} className="flex-1 overflow-y-auto px-5 md:px-8 py-6">
              {BASES_SECTIONS.map((section, si) => (
                <div key={si} className="mb-6">
                  {section.t && (
                    <h4 className="text-amber-400 font-bold text-sm md:text-base tracking-wide mb-3">
                      {section.t}
                    </h4>
                  )}
                  {section.c.map((item, ii) =>
                    Array.isArray(item) ? (
                      <ul key={ii} className="list-disc pl-6 space-y-2 text-sm text-gray-300 leading-relaxed mb-3">
                        {item.map((li, li_i) => (
                          <li key={li_i}>{li}</li>
                        ))}
                      </ul>
                    ) : (
                      <p key={ii} className="text-sm text-gray-300 leading-relaxed mb-3">
                        {item}
                      </p>
                    )
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-500 mt-8 mb-2 text-center">— Fin de las bases —</p>
            </div>

            {/* Pie del modal */}
            <div className="px-5 md:px-8 py-4 border-t border-white/10 bg-zinc-950">
              {!basesRead && (
                <p className="text-xs text-amber-400 text-center mb-3 flex items-center justify-center gap-1">
                  <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  Desplázate hasta el final del documento para poder aceptar las bases
                </p>
              )}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <a
                  href={BASES_PDF_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-gray-400 underline hover:text-white order-2 sm:order-1"
                >
                  Descargar PDF
                </a>
                <button
                  type="button"
                  onClick={acceptBases}
                  disabled={!basesRead}
                  className="order-1 sm:order-2 sm:ml-auto w-full sm:w-auto px-8 py-3 rounded-md bg-[#735707] uppercase text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  He leído y acepto las bases
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ConcursoGuiones;
